import { AuditTemplate } from '@amzn/gbsa-helios-typescript-client';
import {
  Box,
  Button,
  Container,
  Header,
  KeyValuePairs,
  StatusIndicator,
} from '@cloudscape-design/components';
import { memo } from 'react';
import { BusinessUnit } from 'src/types/orbit/BusinessUnit';
import { findBusinessUnitById, formatDateTime } from 'src/utils/generalUtils';

interface TemplateDetailsProps {
  template?: AuditTemplate;
  isEditTemplate: boolean;
  onEditClick: () => void;
  businessUnitList?: BusinessUnit[];
}

const TemplateDetails = ({
  template,
  onEditClick,
  isEditTemplate,
  businessUnitList,
}: TemplateDetailsProps) => {
  if (!template) {
    return null;
  }

  const templateDetails = [
    {
      label: 'Template ID',
      value: template.id || '-',
    },
    {
      label: 'Name',
      value: template.templateName || '-',
    },
    {
      label: 'Description',
      value: template.description || '-',
    },
    {
      label: 'Business Unit',
      value:
        findBusinessUnitById(businessUnitList, template.businessUnit)?.name ||
        template.businessUnit ||
        '-',
    },
    {
      label: 'Division',
      value: template.divisionName || '-',
    },
    {
      label: 'Status',
      value: (
        <StatusIndicator type={template.isActive ? 'success' : 'error'}>
          {template.isActive ? 'Active' : 'Inactive'}
        </StatusIndicator>
      ),
    },
    {
      label: 'Tags',
      value: (
        <div className="tag-container">
          {Object.entries(template.tags).map(([tagGroupName, tagValues]) => {
            return tagValues.map((data, index) => {
              const sanitizedKey = `${tagGroupName}-${data}-${index}`.replace(
                /[^a-zA-Z0-9-]/g,
                '-',
              );
              return (
                <span key={sanitizedKey} className="tag">
                  {tagGroupName} - {data}
                </span>
              );
            });
          })}
        </div>
      ),
    },
    {
      label: 'Locations Included',
      value: template.locationsIncluded ? template.locationsIncluded.join(', ') : '-',
    },
    {
      label: 'Audit Expiration Settings',
      value: `${template.auditExpirationSettings?.months === 0 ? '' : template.auditExpirationSettings?.months + ' Months'}  ${template.auditExpirationSettings?.days} Days`,
    },
    {
      label: 'Created At',
      value: template.createdAt
        ? formatDateTime(
            typeof template.createdAt === 'string'
              ? template.createdAt
              : template.createdAt.toISOString(),
          )
        : '-',
    },
    {
      label: 'Created By',
      value: template.createdBy || '-',
    },
    {
      label: 'Last Updated At',
      value: template.lastUpdatedAt
        ? formatDateTime(
            typeof template.lastUpdatedAt === 'string'
              ? template.lastUpdatedAt
              : template.lastUpdatedAt.toISOString(),
          )
        : '-',
    },
    {
      label: 'Last Updated By',
      value: template.lastUpdatedBy || '-',
    },
  ];

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            isEditTemplate && <Button onClick={onEditClick}> Edit Template</Button>
          }
        >
          Template Details
        </Header>
      }
    >
      <KeyValuePairs columns={3} items={templateDetails} />
    </Container>
  );
};

export default memo(TemplateDetails);
